import { useState, useContext, useRef, useEffect } from 'react';
import { Box, Typography, Paper, Button, CircularProgress, IconButton, Alert } from '@mui/material';
import { ThemeContext } from '../Context';
import { GuardiaAccessChecker } from './GuardiaAccessChecker';
import ImageIcon from '@mui/icons-material/Image';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { logAction, LOG_ACTIONS } from '../../utils/logClient';

function GuardiaVision() {
  const { theme } = useContext(ThemeContext);

  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // États pour gérer l'image et la réponse
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [guardiaAccess, setGuardiaAccess] = useState({ hasAccess: false, reason: '' });
  const [accessChecked, setAccessChecked] = useState(false);
  const fileInputRef = useRef(null);

  // Vérifier l'accès GuardIA au chargement
  useEffect(() => {
    const checkAccess = async () => {
      const access = await GuardiaAccessChecker.checkFeatureAccess('vision');
      setGuardiaAccess(access);
      setAccessChecked(true);
    };
    
    checkAccess();
  }, []);

  // Gestionnaire pour l'upload d'image
  const handleImageUpload = (file) => {
    // Vider les erreurs précédentes
    setError('');

    if (!file) return;

    // Vérifier la taille du fichier (15 Mo maximum)
    const maxSize = 15 * 1024 * 1024; // 15 Mo en octets
    if (file.size > maxSize) {
      setError(`Le fichier est trop volumineux. La taille maximale autorisée est de 15 Mo. Taille actuelle : ${Math.round(file.size / (1024 * 1024))} Mo.`);
      return;
    }

    // Vérifier le format du fichier
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const allowedExtensions = ['.jpeg', '.jpg', '.png'];
    const fileName = file.name.toLowerCase();

    const isValidType = allowedTypes.includes(file.type);
    const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!isValidType || !isValidExtension) {
      setError('Format de fichier non supporté. Seuls les formats JPEG, JPG et PNG sont acceptés.');
      return;
    }

    // Si tout est valide
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  // Gestionnaire pour le drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleImageUpload(files[0]);
    }
  };

  // Gestionnaire pour le drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  // Gestionnaire pour le drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  // Gestionnaire pour le clic sur parcourir
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  // Gestionnaire pour la sélection de fichier
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  // Gestionnaire pour l'envoi
  const handleSend = async () => {
    if (!guardiaAccess.hasAccess) {
      setError(guardiaAccess.reason);
      return;
    }
    
    if (!selectedImage) return;

    setLoading(true);
    setError('');

    try {
      // Convertir l'image en base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Image = e.target.result;

          // Préparer le message avec l'image encodée
          const message = `Voici une image à analyser : [IMAGE:${base64Image}]`;

          // Appeler l'API GuardIA avec le profil GuardiaVision
          const token = sessionStorage.getItem('authToken');
          const response = await fetch('/api/guardia/chat', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              message: message,
              profile: 'GuardiaVision'
            })
          });

          const data = await response.json();

          if (response.ok && data.success) {
            setDescription(data.response);

            // Log de l'analyse d'image avec la réponse de l'IA
            logAction(LOG_ACTIONS.GUARDIA_VISION, null, {
              fileName: selectedImage?.name || null,
              descriptionLength: data.response?.length || 0,
              iaResponse: data.response || null
            });
          } else {
            setError(data.message || 'Erreur lors de l\'analyse de l\'image');
          }
        } catch (error) {
          console.error('Erreur lors de l\'appel à l\'API:', error);
          setError('Erreur de connexion au service GuardIA. Vérifiez que le serveur backend est démarré.');
        } finally {
          setLoading(false);
        }
      };

      reader.onerror = () => {
        setError('Erreur lors de la lecture du fichier image');
        setLoading(false);
      };

      reader.readAsDataURL(selectedImage);
    } catch (error) {
      console.error('Erreur lors de l\'appel à l\'API:', error);
      setError('Erreur de connexion au service GuardIA. Vérifiez que le serveur backend est démarré.');
      setLoading(false);
    }
  };

  // Gestionnaire pour nouvelle image
  const handleNewImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setDescription('');
    setLoading(false);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Gestionnaire pour copier la description
  const handleCopyDescription = async () => {
    if (!description) return;

    try {
      await navigator.clipboard.writeText(description);
      setCopied(true);
      // Reset l'état "copié" après 2 secondes
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  // Si l'accès n'est pas encore vérifié, afficher un loader
  if (!accessChecked) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDark ? '#1a252f' : '#f8f9fa',
        }}
      >
        <CircularProgress sx={{ color: isDark ? '#4fc3f7' : 'primary.main' }} />
      </Box>
    );
  }

  // Si l'accès n'est pas autorisé, afficher un message d'erreur
  if (accessChecked && !guardiaAccess.hasAccess) {
    return (
      <Box 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDark ? '#1a252f' : '#f5f5f5',
          p: 3
        }}
      >
        <Alert 
          severity="warning" 
          sx={{
            backgroundColor: isDark ? 'rgba(255, 193, 7, 0.15)' : undefined,
            color: isDark ? '#ffc107' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#ffc107' : undefined
            },
            maxWidth: 400,
            textAlign: 'center'
          }}
        >
          {guardiaAccess.reason}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: isDark ? '#1a252f' : '#f8f9fa',
        p: 1.5,
      }}
    >
      {/* Section supérieure - Upload et contrôles */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: isDark ? 'rgba(52, 73, 94, 0.7)' : 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
            width: '100%',
            maxWidth: '95%',
          }}
        >

          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 500,
              color: isDark ? '#ecf0f1' : 'text.primary',
              mb: 1
            }}
          >
            <strong>GuardIA</strong> vous aide à établir des constatations sur base d'une image fournie.
          </Typography>

          {/* Zone d'upload d'image */}
          {!selectedImage ? (
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: `2px dashed ${dragOver
                  ? (theme === 'dark' ? '#c084fc' : '#800080')
                  : (isDark ? 'rgba(79, 195, 247, 0.3)' : 'rgba(25, 118, 210, 0.3)')}`,
                borderRadius: 2,
                p: 1.5,
                mb: 1.5,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: dragOver
                  ? (isDark ? 'rgba(192, 132, 252, 0.1)' : 'rgba(128, 0, 128, 0.1)')
                  : 'transparent',
                '&:hover': {
                  borderColor: theme === 'dark' ? '#c084fc' : '#800080',
                  backgroundColor: isDark ? 'rgba(192, 132, 252, 0.05)' : 'rgba(128, 0, 128, 0.05)',
                },
              }}
              onClick={handleBrowseClick}
            >
              <ImageIcon
                sx={{
                  fontSize: 24,
                  color: isDark ? '#c084fc' : '#800080',
                  mb: 0.5,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? '#ecf0f1' : 'text.primary',
                  mb: 0.25,
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  lineHeight: 1.2,
                }}
              >
                Déposez une image ici ou cliquez pour parcourir
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? '#bdc3c7' : 'text.secondary',
                  fontSize: '0.7rem',
                  lineHeight: 1.2,
                }}
              >
                Formats acceptés : JPEG, JPG, PNG (max 15 Mo)
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </Box>
          ) : (
            /* Preview de l'image */
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  mb: 2,
                }}
              >
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 150,
                    borderRadius: 2,
                    boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                />
                <Button
                  size="small"
                  onClick={handleNewImage}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.8)',
                    },
                  }}
                >
                  ✕
                </Button>
              </Box>
            <br />
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? '#bdc3c7' : 'text.secondary',
                  mb: 0.5,
                  fontSize: '0.7rem',
                }}
              >
                {selectedImage.name} ({Math.round(selectedImage.size / 1024)} KB)
              </Typography>
            </Box>
          )}

          {/* Bouton Envoyer */}
          {selectedImage && (
            <Box sx={{ mb: 1 }}>
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={!guardiaAccess.hasAccess || loading}
                sx={{
                  backgroundColor: theme === 'dark' ? '#c084fc' : '#800080',
                  color: 'white',
                  px: 2.5,
                  py: 0.75,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  '&:hover': {
                    backgroundColor: theme === 'dark' ? '#a855f7' : '#6b0050',
                  },
                  '&:disabled': {
                    backgroundColor: isDark ? '#2d3f4c' : 'grey.300',
                    color: isDark ? '#7f8c8d' : 'grey.500',
                  },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Analyse en cours...
                  </>
                ) : (
                  'Envoyer'
                )}
              </Button>
            </Box>
          )}

          {/* Bouton Nouvelle image (quand une image est sélectionnée) */}
          {selectedImage && !loading && (
            <Button
              size="small"
              onClick={handleNewImage}
              sx={{
                color: isDark ? '#95a5a6' : 'text.secondary',
                fontSize: '0.7rem',
                px: 1,
                py: 0.25,
                minHeight: 'auto',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                },
              }}
            >
              Nouvelle image
            </Button>
          )}

          {/* Message d'erreur */}
          {error && (
            <Box
              sx={{
                mt: 1.5,
                p: 1,
                backgroundColor: isDark ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                border: `1px solid ${isDark ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)'}`,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#f44336',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                }}
              >
                {error}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Section analyse - sans cadre */}
      {description && (
        <Box sx={{ mt: 1, px: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: isDark ? '#ecf0f1' : 'text.primary',
                fontSize: '0.8rem',
              }}
            >
              Description de l'image par GuardIA :
            </Typography>

            <IconButton
              onClick={handleCopyDescription}
              size="small"
              sx={{
                color: copied
                  ? (isDark ? '#4ade80' : '#22c55e')
                  : (isDark ? '#95a5a6' : 'text.secondary'),
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                },
                transition: 'color 0.2s ease',
              }}
              title={copied ? 'Copié !' : 'Copier la description'}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: '1rem' }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: '1rem' }} />
              )}
            </IconButton>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: isDark ? '#ecf0f1' : 'text.primary',
              lineHeight: 1.3,
              fontSize: '0.8rem',
            }}
          >
            {description}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default GuardiaVision;
